// Fullwidth header search dialog - opens from the header search button.
//   - Animated typewriter placeholder cycles through example queries
//   - Brand pills update live: typing a brand name (e.g. "Audi") swaps the row
//     for that brand's models (fetched from /wp-json/autoplaza/v1/models)
//   - "Mașini populare" cards are server-rendered into the dialog
import { qs, on, delegate, lockScroll, trapFocus } from '../lib/dom.js';
import { AP_DATA } from '../data/listings.js';

const PLACEHOLDERS = ['Audi Q8', 'BMW X5', 'Hyundai Tucson', 'Mercedes GLC', 'Toyota Camry'];

const REDUCED = typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Top brands by stock - derived from the server-localized AP_DATA.brands.
// The list is delivered alphabetically (A→Z) for the brand selectors, so we
// re-sort a copy by available stock here to surface the most-stocked brands.
function popularBrands(limit = 7) {
  return (AP_DATA.brands || [])
    .slice()
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, limit)
    .map((b) => b.name);
}

function brandFromQuery(q) {
  const lower = String(q || '').trim().toLowerCase();
  if (!lower) return null;
  return (AP_DATA.brands || []).find((b) => lower.startsWith(b.name.toLowerCase())) || null;
}

const modelsCache = new Map();
async function fetchBrandModels(brandName) {
  if (!AP_DATA.apiBase || !brandName) return [];
  if (modelsCache.has(brandName)) return modelsCache.get(brandName);
  let list = [];
  try {
    const res = await fetch(`${AP_DATA.apiBase}models?brand=${encodeURIComponent(brandName)}`, {
      headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
      credentials: 'same-origin',
    });
    list = res.ok ? await res.json() : [];
  } catch { list = []; }
  modelsCache.set(brandName, list);
  return list;
}

export function initHeaderSearch() {
  const dialog = qs('#ap-search-dialog');
  if (!dialog) return;
  // Markup ships with `name="s"` so the form GETs to the catalog as ?s=…
  const input = qs('input[name="s"]', dialog);
  const pillsEl = qs('[data-role="search-pills"]', dialog);
  if (!input || !pillsEl) return;

  let releaseFocus = null;
  let placeholderTimer = 0;
  let pillsToken = 0;

  function renderPills(items, kind) {
    pillsEl.innerHTML = items.map((label) =>
      `<button class="ap-search-dialog__pill" type="button" data-pill="${label}" data-kind="${kind}">${label}</button>`
    ).join('');
  }

  async function updatePills() {
    const v = input.value;
    const token = ++pillsToken;

    // Server-side contextual cascade: brand → models → fuel/year → transmission,
    // always computed from real inventory (see atpl_rest_search_suggest).
    if (AP_DATA.apiBase) {
      try {
        const res = await fetch(`${AP_DATA.apiBase}search-suggest?q=${encodeURIComponent(v)}`, {
          headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
          credentials: 'same-origin',
        });
        if (res.ok) {
          const data = await res.json();
          if (token !== pillsToken) return;
          if (data && Array.isArray(data.pills) && data.pills.length) {
            renderPills(data.pills, data.kind || 'suggest');
            return;
          }
        }
      } catch { /* fall through to the local fallback */ }
      if (token !== pillsToken) return;
    }

    // Fallback (prototype / REST down): local brand → models flow.
    const brand = brandFromQuery(v);
    if (brand) {
      const models = await fetchBrandModels(brand.name);
      if (token !== pillsToken) return;
      if (models.length) {
        renderPills(models.map((m) => `${brand.name} ${m.name}`), 'model');
        return;
      }
    }
    renderPills(popularBrands(), 'brand');
  }

  // Debounced input → suggestions (the open() call still updates instantly).
  let pillsDebounce = 0;
  function updatePillsDebounced() {
    clearTimeout(pillsDebounce);
    pillsDebounce = setTimeout(updatePills, 180);
  }

  function startPlaceholderCycle() {
    if (REDUCED) { input.placeholder = PLACEHOLDERS[0]; return; }
    let phIdx = 0;
    let charIdx = 0;
    let deleting = false;
    function tick() {
      if (input.value) {
        // user is typing - pause the loop, retry after a moment
        placeholderTimer = setTimeout(tick, 600);
        return;
      }
      const target = PLACEHOLDERS[phIdx];
      if (!deleting) {
        charIdx += 1;
        input.placeholder = target.slice(0, charIdx);
        if (charIdx >= target.length) {
          deleting = true;
          placeholderTimer = setTimeout(tick, 1400);
          return;
        }
        placeholderTimer = setTimeout(tick, 70 + Math.random() * 40);
      } else {
        charIdx -= 1;
        input.placeholder = target.slice(0, charIdx);
        if (charIdx <= 0) {
          deleting = false;
          phIdx = (phIdx + 1) % PLACEHOLDERS.length;
          placeholderTimer = setTimeout(tick, 240);
          return;
        }
        placeholderTimer = setTimeout(tick, 38);
      }
    }
    tick();
  }

  function open() {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    lockScroll(true);
    updatePills();
    setTimeout(() => {
      try { input.focus(); } catch { }
    }, 60);
    releaseFocus = trapFocus(dialog, { skipInitialFocus: true });
    startPlaceholderCycle();
  }
  function close() {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    lockScroll(false);
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
    if (placeholderTimer) { clearTimeout(placeholderTimer); placeholderTimer = 0; }
  }

  delegate(document, 'click', '[data-action="open-search"]', (e) => { e.preventDefault(); open(); });
  delegate(dialog, 'click', '[data-action="close-search"]', close);
  on(dialog, 'cancel', (e) => { e.preventDefault(); close(); });
  on(dialog, 'close', () => { lockScroll(false); });
  // Click on backdrop closes
  on(dialog, 'click', (e) => { if (e.target === dialog) close(); });

  // Pill click → set input value, refresh pill row (model pills surface)
  delegate(dialog, 'click', '[data-pill]', (e, btn) => {
    input.value = btn.dataset.pill;
    updatePills();
    input.focus();
  });

  // Frequent suggestion → set value and submit
  delegate(dialog, 'click', '[data-suggest]', (e, btn) => {
    input.value = btn.dataset.suggest;
    qs('form', dialog).submit();
  });

  // Live contextual suggestions
  on(input, 'input', updatePillsDebounced);
}
